import humps from 'humps'

import { defineAction } from '_utils/redux'
import * as agencyService from '_services/agency'
import { BANK_ACCOUNT_FIELDS } from '_utils/constants'
import { notEmptyPayload, parseNumber, formatDateDays } from '_utils/helpers'
import { AGENCY_FIELDS } from '_views/agency/constants'

export const GET_AGENCIES = defineAction('GET_AGENCIES')
export const GET_NEXT_PAGE_AGENCIES = defineAction('GET_NEXT_PAGE_AGENCIES')
export const GET_AGENCIES_FILTERED = defineAction('GET_AGENCIES_FILTERED')
export const GET_AGENCY = defineAction('GET_AGENCY')
export const GET_AGENCY_FULL = defineAction('GET_AGENCY_FULL')
export const UPDATE_AGENCY = defineAction('UPDATE_AGENCY')
export const CREATE_AGENCY = defineAction('CREATE_AGENCY')
export const ADD_AGENCY_STAFF_USER = defineAction('ADD_AGENCY_STAFF_USER')
export const DELETE_AGENCY_STAFF_USER = defineAction('DELETE_AGENCY_STAFF_USER')
export const INACTIVATE_AGENCY_STAFF_USER = defineAction('INACTIVATE_AGENCY_STAFF_USER')
export const UPDATE_AGENCY_STAFF_USER = defineAction('UPDATE_AGENCY_STAFF_USER')
export const GET_AGENCY_RESPONSIBLE = defineAction('GET_AGENCY_RESPONSIBLE')
export const GET_ACCOUNT_MANAGERS = defineAction('GET_ACCOUNT_MANAGERS')
export const GET_AGENCY_DETAILS = defineAction('GET_AGENCY_DETAILS')
export const HANDLE_SET_PAGE = 'HANDLE_SET_PAGE'
export const HANDLE_SET_FILTER = 'HANDLE_SET_FILTER'
export const DELETE_AGENCY_CONTRACT = defineAction('DELETE_AGENCY_CONTRACT')
export const GET_OPTIONS_FIELD_NAME = defineAction('GET_OPTIONS_FIELD_NAME')
export const GET_SECTOR_AGENCY_BY_USER_ID = defineAction('GET_SECTOR_AGENCY_BY_USER_ID')
export const GET_AGENCIES_SIMPLE = defineAction('GET_AGENCIES_SIMPLE')
export const GET_ADDITIONAL_TAKE_RATES_BY_AGENCY = defineAction(
  'GET_ADDITIONAL_TAKE_RATES_BY_AGENCY'
)
export const GET_BUILDINGS = defineAction('GET_BUILDINGS')
export const GET_ACTIVE_BUILDINGS = defineAction('GET_ACTIVE_BUILDINGS')
export const GET_MACRO_STEPS = defineAction('GET_MACRO_STEPS')
export const GET_MACRO_STEPS_CUSTOM = defineAction('GET_MACRO_STEPS_CUSTOM')
export const GET_MACRO_STEP_BY_ID = defineAction('GET_MACRO_STEP_BY_ID')
export const PATCH_MACRO_STEP = defineAction('PATCH_MACRO_STEP')

export const PATCH_AGENCY_USER = defineAction('PATCH_AGENCY_USER')
export const POST_AGENCY_USER = defineAction('POST_AGENCY_USER')

export const GET_AGENCY_ADDITIONAL_CONFIG = defineAction('GET_AGENCY_ADDITIONAL_CONFIG')
export const CREATE_AGENCY_ADDITIONAL_CONFIG = defineAction('CREATE_AGENCY_ADDITIONAL_CONFIG')
export const UPDATE_AGENCY_ADDITIONAL_CONFIG = defineAction('UPDATE_AGENCY_ADDITIONAL_CONFIG')
export const GET_BUILDING_TYPES = defineAction('GET_BUILDING_TYPES')
export const GET_AGENCY_BUILDING_BY_ID = defineAction('GET_AGENCY_BUILDING_BY_ID')
export const POST_AGENCY_BUILDING = defineAction('POST_AGENCY_BUILDING')
export const PATCH_AGENCY_BUILDING = defineAction('PATCH_AGENCY_BUILDING')
export const POST_BUILDING_UNIT = defineAction('POST_BUILDING_UNIT')
export const PATCH_BUILDING_UNIT = defineAction('PATCH_BUILDING_UNIT')
export const GET_BUILDING_UNITS = defineAction('GET_BUILDING_UNITS')
export const GET_BUILDINGS_LIST_BY_USER = defineAction('GET_BUILDINGS_LIST_BY_USER')

export const GET_AGENCY_WEBHOOK_BY_ID = defineAction('GET_AGENCY_WEBHOOK_BY_ID')
export const GET_WEBHOOKS = defineAction('GET_WEBHOOKS')
export const GET_WEBHOOK_QUEUE = defineAction('GET_WEBHOOK_QUEUE')

export const PATCH_WEBHOOK = defineAction('PATCH_WEBHOOK')

export const GET_KANBAN_CONFIG = defineAction('GET_KANBAN_CONFIG')
export const CREATE_KANBAN_CONFIG = defineAction('CREATE_KANBAN_CONFIG')
export const UPDATE_KANBAN_CONFIG = defineAction('UPDATE_KANBAN_CONFIG')
export const GET_MESSAGE_CHANNELS = defineAction('GET_MESSAGE_CHANNELS')
export const CREATE_MESSAGE_CHANNEL = defineAction('CREATE_MESSAGE_CHANNEL')
export const CONNECT_MESSAGE_CHANNEL = defineAction('CONNECT_MESSAGE_CHANNEL')
export const DISCONNECT_MESSAGE_CHANNEL = defineAction('DISCONNECT_MESSAGE_CHANNEL')
export const GET_MESSAGE_CHANNEL_QR_CODE = defineAction('GET_MESSAGE_CHANNEL_QR_CODE')
export const GET_MESSAGE_CHANNEL_STATUS = defineAction('GET_MESSAGE_CHANNEL_STATUS')
export const TEST_MESSAGE_CHANNEL_CONNECTION = defineAction('TEST_MESSAGE_CHANNEL_CONNECTION')

const formatPayload = (values, clearEmptyFields = true) => {
  const formattedPayload = clearEmptyFields ? notEmptyPayload(values) : values

  const payload = Object.entries(formattedPayload).reduce((acc, [key, value]) => {
    if (Object.values(BANK_ACCOUNT_FIELDS).includes(key)) {
      if (key === BANK_ACCOUNT_FIELDS.BANK_ACCOUNT_CPF_CNPJ && value) {
        return { ...acc, bankAccount: { ...acc.bankAccount, [key]: parseNumber(value) } }
      }
      if (key === BANK_ACCOUNT_FIELDS.BANK) {
        return { ...acc, bankAccount: { ...acc.bankAccount, [key]: Number(value) } }
      }
      return { ...acc, bankAccount: { ...acc.bankAccount, [key]: value } }
    }

    return { ...acc, [key]: value }
  }, {})

  payload.bankAccount = humps.decamelizeKeys(payload.bankAccount)

  if (typeof payload[AGENCY_FIELDS.BRAND_LOGO_PNG_URL] === 'string') {
    delete payload[AGENCY_FIELDS.BRAND_LOGO_PNG_URL]
  }

  if (payload[AGENCY_FIELDS.IS_ACTIVE]) {
    payload[AGENCY_FIELDS.IS_ACTIVE] = payload[AGENCY_FIELDS.IS_ACTIVE] === 'true'
  }

  if (payload[AGENCY_FIELDS.PHONE_NUMBER]) {
    payload[AGENCY_FIELDS.PHONE_NUMBER] = parseNumber(payload[AGENCY_FIELDS.PHONE_NUMBER])
  }

  if (payload.phone) {
    payload.phone = parseNumber(payload.phone)
  }

  if (payload[AGENCY_FIELDS.CPF_CNPJ]) {
    payload[AGENCY_FIELDS.CPF_CNPJ] = parseNumber(payload[AGENCY_FIELDS.CPF_CNPJ])
  }

  if (payload[AGENCY_FIELDS.AGENCY_CREATION_DATE]) {
    payload[AGENCY_FIELDS.AGENCY_CREATION_DATE] = formatDateDays(
      payload[AGENCY_FIELDS.AGENCY_CREATION_DATE],
      'dd/MM/yyyy',
      'yyyy-MM-dd'
    )
  }

  if (payload[AGENCY_FIELDS.AGENCY_COMPLETION_PERCENTAGE]) {
    payload[AGENCY_FIELDS.AGENCY_COMPLETION_PERCENTAGE] = payload[
      AGENCY_FIELDS.AGENCY_COMPLETION_PERCENTAGE
    ]
      ?.toString()
      ?.replace(',', '.')
  }
  if (payload[AGENCY_FIELDS.REFERA_COMPLETION_PERCENTAGE]) {
    payload[AGENCY_FIELDS.REFERA_COMPLETION_PERCENTAGE] = payload[
      AGENCY_FIELDS.REFERA_COMPLETION_PERCENTAGE
    ]
      ?.toString()
      ?.replace(',', '.')
  }
  if (payload[AGENCY_FIELDS.MINIMUM_SALE_VALUE]) {
    payload[AGENCY_FIELDS.MINIMUM_SALE_VALUE] = payload[AGENCY_FIELDS.MINIMUM_SALE_VALUE]
      ?.toString()
      ?.replace(',', '.')
  }
  return payload
}

export const getAgencies = params => async (dispatch, getState) => {
  const queryParams = {
    pageSize: params?.pageSize || 1000,
    ...params,
  }
  const data = await agencyService.getAgencies(getState().user.authToken)(queryParams)
  dispatch({
    type: GET_AGENCIES.ACTION,
    payload: async () => data,
    meta: queryParams,
  })
  return data
}

export const getAgenciesSimple = params => (dispatch, getState) => {
  const data = agencyService.getAgenciesSimple(getState().user.authToken)(params)
  dispatch({
    type: GET_AGENCIES_SIMPLE.ACTION,
    payload: data,
  })
  return data
}

export const getAgenciesNextPage = params => async (dispatch, getState) => {
  const queryParams = {
    pageSize: params?.pageSize || getState().agencies.size,
    page: getState().agencies.next,
    ...getState().agencies.filter.toJS(),
  }
  const data = await agencyService.getAgencies(getState().user.authToken)(queryParams)
  dispatch({
    type: GET_NEXT_PAGE_AGENCIES.ACTION,
    payload: async () => data,
  })
  return data
}

export const getAgenciesFiltered =
  (filter = {}) =>
  (dispatch, getState) => {
    dispatch({
      type: GET_AGENCIES_FILTERED.ACTION,
      payload: agencyService.getAgencies(getState().user.authToken)({
        ...getState().agencies.filter.toJS(),
        ...filter,
      }),
    })
  }

export const setTablePage = page => dispatch =>
  dispatch({
    type: HANDLE_SET_PAGE,
    payload: page,
  })

export const setFilter = param => dispatch =>
  dispatch({
    type: HANDLE_SET_FILTER,
    payload: param,
  })

export const getAgency = (agencyId, params) => (dispatch, getState) => {
  const data = agencyService.getAgency(getState().user.authToken)(agencyId, params)
  dispatch({
    type: GET_AGENCY.ACTION,
    payload: data,
  })
  return data
}

export const getAgencyFull = (agencyId, params) => async (dispatch, getState) => {
  const data = await agencyService.getAgencyFull(getState().user.authToken)(agencyId, params)
  dispatch({
    type: GET_AGENCY_FULL.ACTION,
    payload: async () => data,
  })
  return data
}

export const getMessageChannels = agencyId => async (dispatch, getState) => {
  const data = await agencyService.getMessageChannels(getState().user.authToken)(agencyId)
  dispatch({
    type: GET_MESSAGE_CHANNELS.ACTION,
    payload: async () => data,
  })
  return data
}

export const createMessageChannel = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.createMessageChannel(getState().user.authToken)(
    agencyId,
    payload
  )
  dispatch({
    type: CREATE_MESSAGE_CHANNEL.ACTION,
    payload: async () => data,
  })
  return data
}

export const connectMessageChannel = (agencyId, messageChannelId) => async (dispatch, getState) => {
  const data = await agencyService.connectMessageChannel(getState().user.authToken)(
    agencyId,
    messageChannelId
  )
  dispatch({
    type: CONNECT_MESSAGE_CHANNEL.ACTION,
    payload: async () => data,
  })
  return data
}

export const disconnectMessageChannel =
  (agencyId, messageChannelId) => async (dispatch, getState) => {
    const data = await agencyService.disconnectMessageChannel(getState().user.authToken)(
      agencyId,
      messageChannelId
    )
    dispatch({
      type: DISCONNECT_MESSAGE_CHANNEL.ACTION,
      payload: async () => data,
    })
    return data
  }

export const getMessageChannelQrCode =
  (agencyId, messageChannelId) => async (dispatch, getState) => {
    const data = await agencyService.getMessageChannelQrCode(getState().user.authToken)(
      agencyId,
      messageChannelId
    )
    dispatch({
      type: GET_MESSAGE_CHANNEL_QR_CODE.ACTION,
      payload: async () => data,
    })
    return data
  }

export const getMessageChannelStatus =
  (agencyId, messageChannelId) => async (dispatch, getState) => {
    const data = await agencyService.getMessageChannelStatus(getState().user.authToken)(
      agencyId,
      messageChannelId
    )
    dispatch({
      type: GET_MESSAGE_CHANNEL_STATUS.ACTION,
      payload: async () => data,
    })
    return data
  }

export const testMessageChannelConnection =
  (agencyId, messageChannelId, payload) => async (dispatch, getState) => {
    const data = await agencyService.testMessageChannelConnection(getState().user.authToken)(
      agencyId,
      messageChannelId,
      payload
    )
    dispatch({
      type: TEST_MESSAGE_CHANNEL_CONNECTION.ACTION,
      payload: async () => data,
    })
    return data
  }

export const updateAgency =
  (value, clearEmptyFields = true) =>
  async (dispatch, getState) => {
    const agencyId = getState().agencies.currentAgency
    // eslint-disable-next-line no-unused-vars
    const { staffUsers, ...payload } = value
    const data = await agencyService.updateAgency(getState().user.authToken)(
      agencyId,
      formatPayload(payload, clearEmptyFields)
    )

    dispatch({
      type: UPDATE_AGENCY.ACTION,
      payload: async () => data,
    })

    return data
  }
export const createAgency = payload => async (dispatch, getState) => {
  const data = await agencyService.createAgency(getState().user.authToken)(formatPayload(payload))
  dispatch({
    type: CREATE_AGENCY.ACTION,
    payload: async () => data,
  })
  return data
}

export const deleteAgencyFavicon = () => async (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency

  const data = await agencyService.updateAgency(getState().user.authToken)(agencyId, {
    [AGENCY_FIELDS.FAVICON_URL]: null,
  })

  dispatch({
    type: UPDATE_AGENCY.ACTION,
    payload: async () => data,
  })

  return data
}

export const addAgencyStaffUser = payload => async (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency
  const data = await agencyService.addAgencyStaffUser(getState().user.authToken)(agencyId, payload)
  dispatch({
    type: ADD_AGENCY_STAFF_USER.ACTION,
    payload: async () => data,
    meta: { agencyId },
  })
}

export const patchAgencyStaffUser = (userId, payload) => async (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency
  const data = await agencyService.updateAgencyStaffUser(getState().user.authToken)(
    agencyId,
    userId,
    payload
  )
  dispatch({
    type: UPDATE_AGENCY_STAFF_USER.ACTION,
    payload: async () => data,
    meta: { agencyId },
  })
}

export const deleteAgencyStaffUser = staffId => async (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency
  await agencyService.deleteAgencyStaffUser(getState().user.authToken)(agencyId, staffId)
  dispatch({
    type: DELETE_AGENCY_STAFF_USER.FULFILLED,
    meta: { agencyId, staffId },
  })
}

export const inactivateAgencyStaffUser = staffId => async (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency
  const data = await agencyService.inactivateAgencyStaffUser(getState().user.authToken)(
    agencyId,
    staffId
  )
  dispatch({
    type: INACTIVATE_AGENCY_STAFF_USER.FULFILLED,
    payload: data,
    meta: { agencyId },
  })
  return data
}

export const deleteAgencyContract = attachmentId => (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency

  dispatch({
    type: DELETE_AGENCY_CONTRACT.ACTION,
    payload: agencyService.deleteAgencyContract(getState().user.authToken)(agencyId, attachmentId),
    meta: { attachmentId, agencyId },
  })
}

export const updateAgencyStaffUser = values => (dispatch, getState) => {
  const agencyId = getState().agencies.currentAgency
  const currentStaffUser = getState().agencies.results.getIn([agencyId, 'staffUsers', values.id])
  const { id, ...payload } = values
  if (currentStaffUser?.email === payload.email) {
    delete payload.email
  }
  dispatch({
    type: UPDATE_AGENCY_STAFF_USER.ACTION,
    payload: agencyService.updateAgencyStaffUser(getState().user.authToken)(
      agencyId,
      id,
      formatPayload(payload)
    ),
    meta: { agencyId },
  })
}

export const getAgencyResponsible =
  (agencyId, responsibleId = null) =>
  (dispatch, getState) => {
    dispatch({
      type: GET_AGENCY_RESPONSIBLE.ACTION,
      payload: agencyService.getServiceOrderResponsible(getState().user.authToken)(
        agencyId,
        responsibleId
      ),
    })
  }

export const getAccountManagers = agencyId => (dispatch, getState) => {
  dispatch({
    type: GET_ACCOUNT_MANAGERS.ACTION,
    payload: agencyService.getAccountManagers(getState().user.authToken)(agencyId),
    meta: { agencyId },
  })
}

export const getAgencyDetails = agencyId => (dispatch, getState) => {
  const data = agencyService.getAgencyDetails(getState().user.authToken)(agencyId)
  dispatch({
    type: GET_AGENCY_DETAILS.ACTION,
    payload: data,
  })
  return data
}

export const getOptionsFieldName = agencyId => (dispatch, getState) => {
  dispatch({
    type: GET_OPTIONS_FIELD_NAME.ACTION,
    payload: agencyService.getOptionsFieldName(getState().user.authToken)(agencyId),
  })
}

export const getSectorsAgencyByUserId = userAddId => (dispatch, getState) => {
  const data = agencyService.getSectorsAgencyByUserId(getState().user.authToken)(userAddId)
  dispatch({
    type: GET_SECTOR_AGENCY_BY_USER_ID.ACTION,
    payload: data,
  })
  return data
}

export const getAdditionalTakeRatesByAgency = agencyId => (dispatch, getState) => {
  const data = agencyService.getAdditionalTakeRatesByAgency(getState().user.authToken)(agencyId)
  dispatch({
    type: GET_ADDITIONAL_TAKE_RATES_BY_AGENCY.ACTION,
    payload: data,
  })
  return data
}

export const getMacroSteps = params => async (dispatch, getState) => {
  const data = await agencyService.getMacroStepsService(getState().user.authToken)(
    humps.decamelizeKeys(params)
  )

  dispatch({
    type: GET_MACRO_STEPS.ACTION,
    payload: async () => data,
    meta: params,
  })

  return data
}

export const getMacroStepsCustom = params => async (dispatch, getState) => {
  const data = await agencyService.getMacroStepsServiceCustom(getState().user.authToken)(
    humps.decamelizeKeys(params)
  )

  dispatch({
    type: GET_MACRO_STEPS_CUSTOM.ACTION,
    payload: async () => data,
    meta: params,
  })

  return data
}

export const getMacroStepById = macroStepId => async (dispatch, getState) => {
  const data = await agencyService.getMacroStepByIdService(getState().user.authToken)(macroStepId)

  dispatch({
    type: GET_MACRO_STEP_BY_ID.ACTION,
    payload: async () => data,
  })

  return data
}

export const editMacroStep = (id, payload) => async (dispatch, getState) => {
  const data = await agencyService.patchMacroStepToAgencyService(getState().user.authToken)(
    id,
    payload
  )

  dispatch({
    type: PATCH_MACRO_STEP.ACTION,
    payload: async () => data,
  })

  return data
}

export const postMacroStep = payload => async (dispatch, getState) => {
  const data = await agencyService.postMacroStepToAgencyService(getState().user.authToken)(payload)

  dispatch({
    type: PATCH_MACRO_STEP.ACTION,
    payload: async () => data,
  })

  return data
}

export const getAgencyAdditionalConfig = agencyId => async (dispatch, getState) => {
  const data = await agencyService.getAgencyAdditionalConfig(getState().user.authToken)(agencyId)
  dispatch({
    type: GET_AGENCY_ADDITIONAL_CONFIG.ACTION,
    payload: async () => data,
  })

  return data
}

export const getBuildingsByAgency = agencyId => async (dispatch, getState) => {
  const data = await agencyService.getBuildings(getState().user.authToken)(agencyId)

  dispatch({
    type: GET_BUILDINGS.ACTION,
    payload: async () => data,
    meta: agencyId,
  })

  return data
}

export const getActiveBuildingsByAgency = agencyId => async (dispatch, getState) => {
  const data = await agencyService.getActiveBuildingsByAgency(getState().user.authToken)(agencyId)

  dispatch({
    type: GET_ACTIVE_BUILDINGS.ACTION,
    payload: async () => data,
    meta: agencyId,
  })

  return data
}

export const getBuildingTypes = params => (dispatch, getState) => {
  const data = agencyService.getBuildingTypesService(getState().user.authToken)(
    humps.decamelizeKeys(params)
  )
  dispatch({
    type: GET_BUILDING_TYPES.ACTION,
    payload: async () => data,
    meta: params,
  })
  return data
}

export const getBuildingsListByUser = (agencyId, params) => (dispatch, getState) => {
  const data = agencyService.getBuildingsListByUser(getState().user.authToken)(
    agencyId,
    humps.decamelizeKeys(params)
  )
  dispatch({
    type: GET_BUILDINGS_LIST_BY_USER.ACTION,
    payload: async () => data,
    meta: params,
  })
  return data
}

export const createAgencyAdditionalConfig = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.createAgencyAdditionalConfig(getState().user.authToken)(
    agencyId,
    payload
  )
  dispatch({
    type: CREATE_AGENCY_ADDITIONAL_CONFIG.ACTION,
    payload: async () => data,
  })
  return data
}

export const updateAgencyAdditionalConfig = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.updateAgencyAdditionalConfig(getState().user.authToken)(
    agencyId,
    payload
  )
  dispatch({
    type: UPDATE_AGENCY_ADDITIONAL_CONFIG.ACTION,
    payload: async () => data,
  })

  return data
}

export const postAgencyBuilding = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.postAgencyBuildingService(getState().user.authToken)(
    agencyId,
    payload
  )

  dispatch({
    type: POST_AGENCY_BUILDING.ACTION,
    payload: async () => data,
  })

  return data
}

export const patchAgencyBuilding = (agencyId, id, payload) => async (dispatch, getState) => {
  const data = await agencyService.patchAgencyBuildingService(getState().user.authToken)(
    agencyId,
    id,
    payload
  )

  dispatch({
    type: PATCH_AGENCY_BUILDING.ACTION,
    payload: async () => data,
  })

  return data
}

export const getAgencyBuildingById = (agencyId, buildingId) => async (dispatch, getState) => {
  const data = await agencyService.getAgencyBuildingById(getState().user.authToken)(
    agencyId,
    buildingId
  )

  dispatch({
    type: GET_AGENCY_BUILDING_BY_ID.ACTION,
    payload: async () => data,
    meta: agencyId,
  })

  return data
}

export const postBuildingUnit = (agencyId, buildingId, payload) => async (dispatch, getState) => {
  const data = await agencyService.postBuildingUnitService(getState().user.authToken)(
    agencyId,
    buildingId,
    payload
  )

  dispatch({
    type: POST_BUILDING_UNIT.ACTION,
    payload: async () => data,
  })

  return data
}

export const patchBuildingUnit =
  (agencyId, buildingId, id, payload) => async (dispatch, getState) => {
    const data = await agencyService.patchBuildingUnitService(getState().user.authToken)(
      agencyId,
      buildingId,
      id,
      payload
    )

    dispatch({
      type: PATCH_BUILDING_UNIT.ACTION,
      payload: async () => data,
    })

    return data
  }

export const getBuildingUnitsById = (agencyId, buildingId) => async (dispatch, getState) => {
  const data = await agencyService.getBuildingUnitsByIdService(getState().user.authToken)(
    agencyId,
    buildingId
  )

  dispatch({
    type: GET_BUILDING_UNITS.ACTION,
    payload: async () => data,
    meta: agencyId,
  })

  return data
}

export const getAgencyWebhookById = (webhookId, params) => async (dispatch, getState) => {
  const data = await agencyService.getAgencyWebhookById(getState().user.authToken)(
    webhookId,
    params
  )

  dispatch({
    type: GET_AGENCY_WEBHOOK_BY_ID.ACTION,
    payload: async () => data,
    meta: webhookId,
  })

  return data
}

export const patchWebhook = (id, payload) => async (dispatch, getState) => {
  const data = await agencyService.patchWebhookService(getState().user.authToken)(id, payload)

  dispatch({
    type: PATCH_WEBHOOK.ACTION,
    payload: async () => data,
  })

  return data
}

export const getWebhooksByAgency = params => async (dispatch, getState) => {
  const data = await agencyService.getWebhooks(getState().user.authToken)(params)

  dispatch({
    type: GET_WEBHOOKS.ACTION,
    payload: async () => data,
  })

  return data
}

export const getWebhookQueue = (webhookId, params) => async (dispatch, getState) => {
  const data = await agencyService.getWebhookQueue(getState().user.authToken)(webhookId, params)

  dispatch({
    type: GET_WEBHOOK_QUEUE.ACTION,
    payload: async () => data,
  })

  return data
}

// kanban actions

export const getKanbanConfig = (agencyId, params) => async (dispatch, getState) => {
  const data = await agencyService.getKanbanConfig(getState().user.authToken)(
    agencyId,
    humps.decamelizeKeys(params)
  )

  dispatch({
    type: GET_KANBAN_CONFIG.ACTION,
    payload: async () => data,
  })

  return data
}

export const createKanbanConfig = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.createKanbanConfig(getState().user.authToken)(
    agencyId,
    humps.decamelizeKeys(payload)
  )

  dispatch({
    type: CREATE_KANBAN_CONFIG.ACTION,
    payload: async () => data,
  })

  return data
}

export const updateKanbanConfig = (agencyId, payload) => async (dispatch, getState) => {
  const data = await agencyService.updateKanbanConfig(getState().user.authToken)(
    agencyId,
    humps.decamelizeKeys(payload)
  )

  dispatch({
    type: UPDATE_KANBAN_CONFIG.ACTION,
    payload: async () => data,
  })

  return data
}
